import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

function formatPrice(amount, currency, locale = "en") {
    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: (currency || "EUR").toUpperCase(),
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return `${amount} ${currency}`;
    }
}

// Compact pill that links to the cheapest matching available listing.
// Shown next to the affiliate buttons on every BookCard when there's stock.
export default function UsedListingPill({ book, locale = "en", testIdSuffix = "" }) {
    const { t } = useTranslation();
    const [match, setMatch] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/marketplace/listings/match", {
                    params: {
                        title: book.title,
                        author: book.author,
                        isbn: book.isbn || undefined,
                    },
                });
                if (!alive) return;
                if (Array.isArray(data) && data.length > 0) setMatch(data[0]);
            } catch {
                /* swallow */
            }
        })();
        return () => { alive = false; };
    }, [book.title, book.author, book.isbn]);

    if (!match) return null;
    const price = formatPrice(match.price, match.currency, locale);

    return (
        <Link
            to={`/marketplace?id=${match.id}`}
            data-testid={`used-pill${testIdSuffix}`}
            className="col-span-2 flex items-center justify-center gap-1.5 border border-[var(--accent)]/60 hover:border-[var(--accent)] hover:bg-[var(--bg-hover)] text-[var(--accent)] rounded text-xs py-2 px-2 transition-all uppercase tracking-wider"
        >
            <ShoppingBag size={12} strokeWidth={1.5} />
            <span>{t("marketplace.used_from", { price })}</span>
        </Link>
    );
}
